import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";

import { cn } from "../../../lib";

const stackedListVariants = cva("divide-y divide-gray-100", {
  variants: {
    variant: {
      default: "",
      none: "",
    },
    size: {
      default: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

interface StackedListProps
  extends React.HTMLAttributes<HTMLUListElement>,
    VariantProps<typeof stackedListVariants> {
  items: {
    id: string;
    title?: string;
    subtitle?: string;
    rightTitle?: string;
    rightSubtitle?: string;
    icon?: any;
  }[];
  onPress: (conferenceId: string) => unknown;
}

const StackedList = forwardRef<HTMLButtonElement, StackedListProps>(
  (props, _) => {
    const {
      variant = "default",
      size = "default",
      className,
      items,
      onPress,
      ...rest
    } = props;
    return (
      <ul
        role="list"
        className={cn(stackedListVariants({ variant, size }), className)}
        {...rest}
      >
        {items?.map((item, index) => (
          <li
            key={`${index}`}
            className="flex justify-between gap-x-6 py-5 px-5 cursor-pointer hover:bg-gray-50"
            onClick={() => onPress(item.id ?? "")}
          >
            <div className="flex min-w-0 gap-x-4">
              <img
                className="h-12 w-12 flex-none rounded-full bg-gray-50"
                src={item.icon}
                alt={item.title ?? ""}
                />
              <div className="min-w-0 flex-col flex-grow">
                <p className="text-sm font-semibold leading-6 text-gray-900">
                  {item.title}
                </p>
                <p className="mt-1 truncate text-xs leading-5 text-gray-500">
                  {item.subtitle}
                </p>
              </div>
            </div>
            <div className="hidden shrink-0 lg:flex lg:flex-col lg:items-end lg:justify-end lg:flex-grow">
              <p className="text-sm leading-6 text-gray-900">
                {item.rightTitle}
              </p>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                {item.rightSubtitle}
              </p>
            </div>
          </li>
        ))}
      </ul>
    );
  },
);

StackedList.displayName = "StackedList";

export default StackedList;
export { stackedListVariants };
export type { StackedListProps };
